/* Muñequita Vintage — white loading screen with the logo, shown while a
   click navigates to a new page ("when clicking anything i want a white
   loading screen with the logo to be shown, everytime when you click an
   item, menu option, user, cart, etc", 2026-09-02).

   Design: show the overlay the instant a real navigating click happens,
   then hide it once the destination page has rendered. Two listeners do
   the hiding: `pageshow` covers both a normal fresh page load AND a
   back/forward-cache restore (Safari/Firefox often restore from bfcache
   without re-firing `load`), and a short timeout is a safety net so the
   overlay can never get stuck visible if something unexpected happens (a
   failed navigation, a same-page link, etc).

   Deliberately does NOT use the capture phase: this listener is bound on
   `document` in the bubble phase, so it always runs *after* any
   feature-specific listener bound closer to the clicked element (facets
   filtering, "load more" pagination, quick-add, etc. — several of these
   intercept a real-looking `<a href>` click with their own
   `preventDefault()` and handle it via fetch() instead of a real page
   navigation). Checking `event.defaultPrevented` after those have already
   run means the loader only shows for clicks that actually go on to
   navigate the browser away from this page.

   Minimum-visible-duration: `showOverlay()` stamps the time it was shown;
   `hideOverlay()` checks how long that's been and, if it's under
   MIN_VISIBLE_MS, defers the actual hide until that minimum has elapsed
   instead of hiding immediately. Every caller (pageshow, the
   account-dialog `open` handler, the safety-net timeouts) goes through
   this same `hideOverlay()` gate, so none of them need their own awareness
   of the minimum.

   Forced-paint navigation: a real navigating click prevents the link's
   default action, waits for a *guaranteed* paint (two nested
   requestAnimationFrame callbacks — the first runs before the browser
   computes the next frame, the second only after that frame has actually
   been rendered) plus a short extra dwell, and only then performs the
   navigation itself via `window.location.href`. Without this, the browser
   is free to start tearing down the page as soon as the click event
   finishes dispatching, before ever painting the frame where `is-visible`
   was just added — on a fast/cached destination the loader could end up
   never actually being seen at all. A hard timeout backstops the sequence
   in case requestAnimationFrame never fires (it's paused in a
   backgrounded/hidden tab) — a click must never fail to navigate.

   Cross-page continuity (added 2026-09-09, follow-up: "in the preview
   version the loading screen is showing up and disappearing almost
   instantly, i need people to actually see it"): all of the above only
   controls the loader's behaviour *within a single page's lifetime*.
   But every click here that actually navigates lands on a brand-new
   document — a fresh load of this very script, with `shownAt` back at 0
   and the overlay's default (hidden) markup freshly rendered. So even
   though the OLD page dutifully held the loader open for a bit, the
   MIN_VISIBLE_MS timer on the NEW page had nothing to measure from: its
   `pageshow` fires, sees the overlay hidden (because on a fresh document
   it always starts hidden), and does nothing — meaning the *only* time
   the loader was ever actually visible was however briefly the OLD page
   held it before tearing down, regardless of MIN_VISIBLE_MS. That's
   exactly why it kept reading as "instant" even after both fixes above.

   Fixed with a tiny synchronous (non-deferred) script in
   layout/theme.liquid, placed right after the `#brand-page-loader` markup:
   `showOverlay()` here now also writes its timestamp to
   `sessionStorage.brandLoaderShownAt` right before navigating away. That
   inline script runs on the new page *before first paint* (this file is
   `defer`, so it only runs later), and if the flag is present it marks
   the loader visible immediately — with its transition disabled so it's
   already fully shown, not fading in again — then hands the original
   timestamp to this script via `window.__brandLoaderShownAt` and clears
   the sessionStorage flag. On load, this script adopts that original
   timestamp as its own `shownAt` (instead of starting fresh at 0) and
   re-enables the transition on the next frame, so the loader now stays
   continuously visible across the entire navigation and MIN_VISIBLE_MS is
   finally measured from the moment the user actually clicked — not from
   whenever the new page happened to finish loading. */
(function () {
  var overlayEl;
  var hideTimeoutId; // safety-net timer, in case nothing else ever hides the overlay
  var minVisibleTimeoutId; // pending deferred hide, while under MIN_VISIBLE_MS
  var shownAt = 0;
  var MIN_VISIBLE_MS = 650;
  var NAV_PAINT_HOLD_MS = 120; // extra dwell after the guaranteed paint, before navigating away
  var NAV_HARD_TIMEOUT_MS = 700; // never block navigation longer than this, even if rAF never fires

  function overlay() {
    if (!overlayEl) overlayEl = document.getElementById('brand-page-loader');
    return overlayEl;
  }

  // If the inline script in layout/theme.liquid already marked the loader
  // visible (because we arrived here via a click on the previous page),
  // adopt its original timestamp instead of starting a fresh one, and
  // restore the normal CSS transition it deliberately suppressed to avoid
  // a fade-in flash on this page's very first frame. Backstopped by a
  // timeout, not just rAF: rAF is paused in a backgrounded/unfocused tab
  // (e.g. a link opened without ever gaining focus), and without a
  // fallback the fade-out transition would stay stuck disabled for that
  // tab's whole lifetime, making the eventual hide look like an instant
  // snap instead of the intended smooth fade.
  if (window.__brandLoaderShownAt) {
    shownAt = window.__brandLoaderShownAt;
    (function () {
      var el = overlay();
      if (!el) return;
      var cleared = false;
      function clearTransitionOverride() {
        if (cleared) return;
        cleared = true;
        el.style.transition = '';
      }
      requestAnimationFrame(clearTransitionOverride);
      window.setTimeout(clearTransitionOverride, 300);
    })();
  }

  function showOverlay() {
    var el = overlay();
    if (!el) return;
    if (minVisibleTimeoutId) {
      window.clearTimeout(minVisibleTimeoutId);
      minVisibleTimeoutId = null;
    }
    shownAt = Date.now();
    try {
      sessionStorage.setItem('brandLoaderShownAt', String(shownAt));
    } catch (e) {}
    el.classList.add('is-visible');
    el.setAttribute('aria-hidden', 'false');
  }

  function reallyHideOverlay() {
    var el = overlay();
    if (!el) return;
    el.classList.remove('is-visible');
    el.setAttribute('aria-hidden', 'true');
    if (hideTimeoutId) {
      window.clearTimeout(hideTimeoutId);
      hideTimeoutId = null;
    }
    try {
      sessionStorage.removeItem('brandLoaderShownAt');
    } catch (e) {}
  }

  function hideOverlay() {
    var el = overlay();
    if (!el) return;
    if (!el.classList.contains('is-visible')) return; // already hidden, nothing to defer

    var elapsed = Date.now() - shownAt;
    var remaining = MIN_VISIBLE_MS - elapsed;

    if (remaining > 0) {
      if (minVisibleTimeoutId) window.clearTimeout(minVisibleTimeoutId);
      minVisibleTimeoutId = window.setTimeout(function () {
        minVisibleTimeoutId = null;
        reallyHideOverlay();
      }, remaining);
      return;
    }

    reallyHideOverlay();
  }

  // Navigates to `href` only once the browser has actually painted a frame
  // (guaranteed by the two nested rAFs) plus a short deliberate hold, so a
  // click can never be "too fast to see" regardless of how quickly the
  // destination would otherwise load. Backstopped by a hard timeout in
  // case rAF never fires (a backgrounded tab pauses it) — navigation must
  // always eventually happen.
  function navigateAfterPaint(href) {
    var navigated = false;
    function go() {
      if (navigated) return;
      navigated = true;
      window.location.href = href;
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        window.setTimeout(go, NAV_PAINT_HOLD_MS);
      });
    });
    window.setTimeout(go, NAV_HARD_TIMEOUT_MS);
  }

  function isRealNavigationClick(event) {
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false; // left click only
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false; // opens elsewhere / modified click

    var link = event.target.closest ? event.target.closest('a[href]') : null;
    if (!link) return false;
    if (link.target && link.target !== '_self') return false; // e.g. target="_blank" (Instagram, etc.)
    if (link.hasAttribute('download')) return false;

    var href = link.getAttribute('href');
    if (!href || href.charAt(0) === '#') return false;
    if (
      href.indexOf('mailto:') === 0 ||
      href.indexOf('tel:') === 0 ||
      href.indexOf('sms:') === 0 ||
      href.indexOf('javascript:') === 0
    ) {
      return false;
    }

    try {
      var url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return false; // external link, leaves the tab/this document
      // Same page, different hash only — not a real navigation.
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) {
        return false;
      }
    } catch (e) {
      return false;
    }

    return true;
  }

  document.addEventListener('click', function (event) {
    if (isRealNavigationClick(event)) {
      var link = event.target.closest('a[href]');
      event.preventDefault();
      showOverlay();
      hideTimeoutId = window.setTimeout(hideOverlay, 6000); // safety net only
      navigateAfterPaint(link.href);
      return;
    }

    // The account icon (<shopify-account>) opens an on-page dialog rather
    // than navigating to a new page, but the request explicitly called
    // out "user" alongside menu/cart — show it briefly here too. The
    // `open` listener already in layout/theme.liquid fires once the
    // dialog is actually visible; hide the loader at that same moment
    // (see below), with a short timeout as a fallback in case that event
    // doesn't fire for some reason (e.g. the dialog fails to open).
    var accountIcon = event.target.closest ? event.target.closest('shopify-account, .header__icon--account') : null;
    if (accountIcon) {
      showOverlay();
      hideTimeoutId = window.setTimeout(hideOverlay, 1200);
    }
  });

  // Hides the loader once the shopify-account dialog is actually open —
  // see the capture note on this same event in sections/header.liquid.
  document.addEventListener(
    'open',
    function (event) {
      if (event.target instanceof HTMLElement && event.target.matches('shopify-account')) {
        hideOverlay();
      }
    },
    true
  );

  // Covers a normal fresh page load and a back/forward-cache restore.
  window.addEventListener('pageshow', hideOverlay);
})();
